import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CREW_ROLE_LABELS,
  DEFAULT_RAILCO,
  TRAIN_KINDS,
  fareBand,
  formatPence,
  nameProblem,
  railStopsOf,
  weekPlan,
  type RailCoContent,
  type RailCompanyReply,
  type RailCompanyView,
  type RailRoute,
  type TrainKind,
  type TrainView,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { useCatalogue } from '../lib/content';
import { actionKey, londonWhen } from '../lib/format';

const PARKED: Record<NonNullable<TrainView['parked']>, string> = {
  own: 'Parked',
  crew: 'No crew',
  worn: 'Worn out',
};
const conditionTone = (condition: number) => (condition >= 70 ? 'good' : condition >= 40 ? 'fair' : 'poor');

// Rail Co., as Lagos Life's Bus Co.: register a train company (name, paint, slogan), buy trains for routes out of
// King's Cross, crew them, set fares, keep them serviced. The evenings' walk-ins pay from 6 pm; the bills go out
// every Saturday at 9 am. The books are worked out up to now each time this opens.
export function RailCoScreen({ onClose, onWallet }: { onClose: () => void; onWallet: () => void }) {
  const catalogue = useCatalogue();
  const content = catalogue?.railco ?? DEFAULT_RAILCO;
  const routes = useMemo<RailRoute[]>(
    () =>
      catalogue
        ? railStopsOf(catalogue.rail)
            .filter((stop) => catalogue.place(stop.placeId)?.visible)
            .map((stop) => ({ placeId: stop.placeId, name: stop.name, farePence: stop.farePence }))
        : [],
    [catalogue],
  );
  const [reply, setReply] = useState<RailCompanyReply | null>(null);
  const [failure, setFailure] = useState<ReturnType<typeof describeError> | null>(null);
  const [busy, setBusy] = useState(false);
  const [buying, setBuying] = useState(false);
  const load = useCallback(async () => {
    try {
      setReply(await api.railco.mine());
      setFailure(null);
    } catch (error) {
      setFailure(describeError(error));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  const run = async (work: () => Promise<RailCompanyReply>) => {
    setBusy(true);
    try {
      const next = await work();
      setReply(next);
      if (next.message) notify(next.message);
      onWallet();
      return true;
    } catch (error) {
      notify(describeError(error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const company = reply?.company ?? null;
  return (
    <div
      className="profile-screen business-screen railco-screen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="railco-title"
    >
      <div className="profile-frame">
        <header className="profile-header">
          {buying ? (
            <button type="button" className="profile-back" onClick={() => setBuying(false)}>
              ‹ Rail Co.
            </button>
          ) : (
            <button type="button" className="profile-back" onClick={onClose}>
              ‹ Phone
            </button>
          )}
          <h2 id="railco-title">{buying ? 'Buy a train' : '🚆 Rail Co.'}</h2>
          <button type="button" className="life-close" aria-label="Close Rail Co." onClick={onClose}>
            ×
          </button>
        </header>
        <div className="profile-body">
          {failure && <ErrorNotice error={failure} onRetry={() => void load()} />}
          {!reply && !failure && <p className="business-muted">Opening the books…</p>}
          {reply && !company && (
            <StartCompany
              content={content}
              routes={routes}
              walletPence={reply.walletPence}
              busy={busy}
              onStart={(body) => void run(() => api.railco.start(body, actionKey('railco')))}
            />
          )}
          {reply && company && buying && (
            <BuyTrain
              content={content}
              routes={routes}
              company={company}
              walletPence={reply.walletPence}
              busy={busy}
              onBuy={async (body) => {
                if (await run(() => api.railco.buy(body, actionKey('railco')))) setBuying(false);
              }}
            />
          )}
          {reply && company && !buying && (
            <Company
              content={content}
              routes={routes}
              company={company}
              walletPence={reply.walletPence}
              busy={busy}
              onBuy={() => setBuying(true)}
              onTrain={(id, body) => void run(() => api.railco.train(id, body, actionKey('railco')))}
              onAct={(body) => void run(() => api.railco.act(body, actionKey('railco')))}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// the train in its paint, the company's name on the side and the slogan on the back
function TrainPreview({ name, body, stripe, slogan }: { name: string; body: string; stripe: string; slogan?: string }) {
  return (
    <div
      className="railco-train"
      style={{ ['--body' as string]: body, ['--stripe' as string]: stripe }}
      aria-hidden="true"
    >
      <span className="railco-windows">
        {Array.from({ length: 6 }, (_, i) => (
          <i key={i} />
        ))}
      </span>
      <strong>{name || 'Your company'}</strong>
      {slogan && <small>{slogan}</small>}
    </div>
  );
}

function Liveries({
  content,
  value,
  onPick,
}: {
  content: RailCoContent;
  value: { body: string; stripe: string };
  onPick: (value: { body: string; stripe: string }) => void;
}) {
  return (
    <div className="railco-liveries" role="radiogroup" aria-label="Paint job">
      {content.liveries.map((livery) => {
        const on = livery.body === value.body && livery.stripe === value.stripe;
        return (
          <button
            type="button"
            key={livery.name}
            role="radio"
            aria-checked={on}
            aria-label={livery.name}
            className={on ? 'active' : ''}
            style={{ ['--body' as string]: livery.body, ['--stripe' as string]: livery.stripe }}
            onClick={() => onPick({ body: livery.body, stripe: livery.stripe })}
          >
            <i />
          </button>
        );
      })}
    </div>
  );
}

// "What would one train make?": a kind, a route and a fare; an ordinary week of the station's walk-ins
function Planner({ content, routes }: { content: RailCoContent; routes: RailRoute[] }) {
  const [kind, setKind] = useState<TrainKind>('commuter');
  const [routeId, setRouteId] = useState(routes.at(-1)?.placeId ?? '');
  const [percent, setPercent] = useState(100);
  const route = routes.find((item) => item.placeId === routeId) ?? routes[0];
  if (!route) return null;
  const band = fareBand(content, route, kind);
  const plan = weekPlan(content, { kind, route, farePence: Math.round((band.ref * percent) / 100) });
  const train = content.trains[kind];
  return (
    <section className="railco-planner" aria-label="What would one train make?">
      <strong className="business-step-title">WHAT WOULD ONE TRAIN MAKE?</strong>
      <div className="business-chips" role="radiogroup" aria-label="Train">
        {TRAIN_KINDS.map((item) => (
          <button
            type="button"
            key={item}
            role="radio"
            aria-checked={kind === item}
            className={kind === item ? 'active' : ''}
            onClick={() => setKind(item)}
          >
            {content.trains[item].icon} {content.trains[item].label}
          </button>
        ))}
      </div>
      <div className="business-chips" role="radiogroup" aria-label="Route">
        {routes.map((item) => (
          <button
            type="button"
            key={item.placeId}
            role="radio"
            aria-checked={route.placeId === item.placeId}
            className={route.placeId === item.placeId ? 'active' : ''}
            onClick={() => setRouteId(item.placeId)}
          >
            {item.name}
          </button>
        ))}
      </div>
      <label className="railco-fare">
        <span>
          Fare <strong>{formatPence(plan.fare)}</strong>{' '}
          <small>({Math.round((plan.fare / band.ref) * 100)}% of the going rate)</small>
        </span>
        <input
          type="range"
          min={Math.round(content.rules.fareLow * 100)}
          max={Math.round(content.rules.fareHigh * 100)}
          value={percent}
          aria-label="Fare"
          onChange={(e) => setPercent(Number(e.target.value))}
        />
      </label>
      <div className="railco-cash">
        <small>Cash a week</small>
        <strong className={plan.cash >= 0 ? 'up' : 'down'}>
          {plan.cash >= 0 ? '+' : ''}
          {formatPence(plan.cash)}
        </strong>
      </div>
      <p className="business-muted">
        About {plan.ridersPerEvening} walk-ins an evening of {train.seats} seats ·{' '}
        {plan.paybackWeeks ? `pays back in ${plan.paybackWeeks} weeks` : 'never pays back'} · loses{' '}
        {formatPence(plan.lostValue)} of value a week
      </p>
      <p className="business-muted">
        One {train.label.toLowerCase()} at list price ({formatPence(train.pricePence)}), an ordinary week, the station's
        walk-ins only. Fridays and Sundays are busier; a dear fare fills fewer seats.
      </p>
    </section>
  );
}

function StartCompany({
  content,
  routes,
  walletPence,
  busy,
  onStart,
}: {
  content: RailCoContent;
  routes: RailRoute[];
  walletPence: number;
  busy: boolean;
  onStart: (body: { name: string; slogan?: string; body: string; stripe: string }) => void;
}) {
  const [name, setName] = useState('');
  const [slogan, setSlogan] = useState('');
  const [paint, setPaint] = useState({ body: content.liveries[0]!.body, stripe: content.liveries[0]!.stripe });
  const problem = name.trim() ? nameProblem(content, name) : null;
  const short = walletPence < content.rules.registerPence;
  if (!content.visible) return <p className="business-muted">Train companies open soon.</p>;
  return (
    <div className="business-start">
      <section className="business-hero railco-hero">
        <span aria-hidden="true">🚆</span>
        <strong>Start a train company</strong>
        <span>
          Buy trains, hire crews, set your fares. Riders at King&apos;s Cross pick the best deal, and other players ride
          your trains too.
        </span>
      </section>
      <TrainPreview name={name.trim()} body={paint.body} stripe={paint.stripe} slogan={slogan.trim()} />
      <label className="railco-field">
        <span>COMPANY NAME</span>
        <input value={name} maxLength={24} placeholder="Bermondsey Rail" onChange={(e) => setName(e.target.value)} />
        <small className={problem ? 'railco-problem' : ''}>
          {problem ?? `${name.trim().length}/24 · real operators' names are taken`}
        </small>
      </label>
      <div className="railco-field">
        <span>PAINT JOB</span>
        <Liveries content={content} value={paint} onPick={setPaint} />
      </div>
      <label className="railco-field">
        <span>BACK-OF-TRAIN SLOGAN (OPTIONAL)</span>
        <input
          value={slogan}
          maxLength={40}
          placeholder="Next stop, everywhere"
          onChange={(e) => setSlogan(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="city-card-go"
        disabled={busy || !name.trim() || Boolean(problem) || short}
        onClick={() => onStart({ name: name.trim(), slogan: slogan.trim() || undefined, ...paint })}
      >
        {short
          ? `Need ${formatPence(content.rules.registerPence)}`
          : `Register · ${formatPence(content.rules.registerPence)}`}
      </button>
      <p className="business-muted">Crews are paid every Saturday at 9 AM. Walk-ins pay every evening from 6 PM.</p>
      <Planner content={content} routes={routes} />
    </div>
  );
}

function BuyTrain({
  content,
  routes,
  company,
  walletPence,
  busy,
  onBuy,
}: {
  content: RailCoContent;
  routes: RailRoute[];
  company: RailCompanyView;
  walletPence: number;
  busy: boolean;
  onBuy: (body: { kind: TrainKind; route: string; withOptional: boolean }) => void;
}) {
  const [kind, setKind] = useState<TrainKind>('commuter');
  const [routeId, setRouteId] = useState(routes.at(-1)?.placeId ?? '');
  const [withOptional, setWithOptional] = useState(true);
  const train = content.trains[kind];
  const route = routes.find((item) => item.placeId === routeId) ?? routes[0];
  if (!route) return <p className="business-muted">No trains run right now.</p>;
  const plan = weekPlan(content, { kind, route, withOptional, insured: company.insured });
  const crew = [...train.required, ...(withOptional && train.optional ? [train.optional] : [])];
  const weekly =
    crew.reduce((sum, item) => sum + item.wagePence, 0) + train.accessPence + (company.insured ? train.insurePence : 0);
  const full = company.trains.length >= content.rules.maxTrains;
  const short = walletPence < train.pricePence;
  return (
    <div className="business-start">
      <TrainPreview
        name={company.name}
        body={company.body}
        stripe={company.stripe}
        slogan={company.slogan ?? undefined}
      />
      <fieldset className="business-step">
        <legend>Which train</legend>
        <div className="business-kinds">
          {TRAIN_KINDS.map((item) => {
            const type = content.trains[item];
            return (
              <button
                type="button"
                key={item}
                aria-pressed={kind === item}
                className={kind === item ? 'active' : ''}
                onClick={() => setKind(item)}
              >
                <span aria-hidden="true">{type.icon}</span>
                <strong>{type.label}</strong>
                <small>
                  {type.seats} seats · {formatPence(type.pricePence)}
                </small>
                <small>{type.perk}</small>
              </button>
            );
          })}
        </div>
      </fieldset>
      <fieldset className="business-step">
        <legend>Where it runs from King&apos;s Cross</legend>
        <div className="business-chips" role="radiogroup" aria-label="Route">
          {routes.map((item) => (
            <button
              type="button"
              key={item.placeId}
              role="radio"
              aria-checked={route.placeId === item.placeId}
              className={route.placeId === item.placeId ? 'active' : ''}
              onClick={() => setRouteId(item.placeId)}
            >
              {item.name} <small>{formatPence(fareBand(content, item, kind).ref)}</small>
            </button>
          ))}
        </div>
      </fieldset>
      <section className="business-sums" aria-label="The crew and the week">
        {crew.map((item) => (
          <p key={item.role}>
            <span>{CREW_ROLE_LABELS[item.role]}</span>
            <span>{formatPence(item.wagePence)} a week</span>
          </p>
        ))}
        <p>
          <span>Track access</span>
          <span>{formatPence(train.accessPence)} a week</span>
        </p>
        {company.insured && (
          <p>
            <span>Insurance</span>
            <span>{formatPence(train.insurePence)} a week</span>
          </p>
        )}
        <p className="total">
          <span>Bills, Saturday 9 AM</span>
          <span>{formatPence(weekly)}</span>
        </p>
        {train.optional && (
          <label className="railco-toggle">
            <input type="checkbox" checked={withOptional} onChange={(e) => setWithOptional(e.target.checked)} />
            Hire a {CREW_ROLE_LABELS[train.optional.role].toLowerCase()} (+{Math.round(train.optional.penalty * 100)}%
            riders)
          </label>
        )}
        <small>
          About {plan.ridersPerEvening} walk-ins an evening ·{' '}
          {plan.cash >= 0 ? `+${formatPence(plan.cash)}` : formatPence(plan.cash)} a week ·{' '}
          {plan.paybackWeeks ? `pays back in ~${plan.paybackWeeks} weeks` : 'never pays back'}
        </small>
      </section>
      <button
        type="button"
        className="city-card-go"
        disabled={busy || full || short}
        onClick={() => onBuy({ kind, route: route.placeId, withOptional: withOptional && Boolean(train.optional) })}
      >
        {full
          ? `Fleet full · ${content.rules.maxTrains} trains`
          : short
            ? `Need ${formatPence(train.pricePence)}`
            : `Buy · ${formatPence(train.pricePence)}`}
      </button>
      <p className="business-muted">
        It runs from tonight&apos;s 6 PM. Sell it back any time for {Math.round(content.rules.resaleShare * 100)}% of
        the price, a little less each week.
      </p>
    </div>
  );
}

function Company({
  content,
  routes,
  company,
  walletPence,
  busy,
  onBuy,
  onTrain,
  onAct,
}: {
  content: RailCoContent;
  routes: RailRoute[];
  company: RailCompanyView;
  walletPence: number;
  busy: boolean;
  onBuy: () => void;
  onTrain: (id: string, body: Parameters<typeof api.railco.train>[1]) => void;
  onAct: (body: Parameters<typeof api.railco.act>[0]) => void;
}) {
  const routeName = (id: string) => routes.find((item) => item.placeId === id)?.name ?? id;
  const lines = [...new Set(company.trains.map((train) => `${train.route}:${train.kind}`))];
  const full = company.trains.length >= content.rules.maxTrains;
  const [renaming, setRenaming] = useState(false);
  return (
    <div className="business-detail">
      <section className="business-hero railco-hero">
        <TrainPreview
          name={company.name}
          body={company.body}
          stripe={company.stripe}
          slogan={company.slogan ?? undefined}
        />
        <span>
          {company.stars
            ? `★ ${company.stars.toFixed(1)} · ${company.ratings} rating${company.ratings === 1 ? '' : 's'}`
            : 'No ratings yet'}
          {' · '}
          {company.trains.length} train{company.trains.length === 1 ? '' : 's'} · worth{' '}
          {formatPence(company.fleetValuePence)}
        </span>
      </section>
      <section className="business-sums" aria-label="This week's bills">
        <p>
          <span>Crew wages</span>
          <span>{formatPence(company.bill.wages)}</span>
        </p>
        <p>
          <span>Track access</span>
          <span>{formatPence(company.bill.access)}</span>
        </p>
        {company.engineers > 0 && (
          <p>
            <span>{company.engineers === 1 ? content.engineerName : `${company.engineers} engineers`}</span>
            <span>{formatPence(company.bill.engineers)}</span>
          </p>
        )}
        {company.insured && (
          <p>
            <span>Insurance</span>
            <span>{formatPence(company.bill.insurance)}</span>
          </p>
        )}
        <p className="total">
          <span>Due {londonWhen(company.bill.due)}</span>
          <span>{formatPence(company.bill.total)}</span>
        </p>
        <small>
          Walk-ins pay every evening from 6 PM. Keep money in your wallet for Saturday: unpaid crews walk off.
        </small>
      </section>
      <ul className="business-cards railco-fleet" aria-label="Your trains">
        {company.trains.map((train) => (
          <TrainCard
            key={train.id}
            content={content}
            train={train}
            routes={routes}
            routeName={routeName}
            walletPence={walletPence}
            busy={busy}
            onAct={(body) => onTrain(train.id, body)}
          />
        ))}
      </ul>
      {!company.trains.length && (
        <p className="business-muted">No trains yet. Buy one to start running from King&apos;s Cross.</p>
      )}
      <button type="button" className="city-card-go" disabled={busy || full} onClick={onBuy}>
        {full ? `Fleet full · ${content.rules.maxTrains} trains` : '➕ Buy a train'}
      </button>
      {lines.length > 0 && (
        <section className="railco-fares" aria-label="Fares">
          <strong className="business-step-title">FARES</strong>
          {company.fareLockedUntil && (
            <p className="business-muted">Next fare change at {londonWhen(company.fareLockedUntil)}.</p>
          )}
          {lines.map((line) => {
            const [routeId, kind] = line.split(':') as [string, TrainKind];
            const route = routes.find((item) => item.placeId === routeId);
            if (!route) return null;
            return (
              <FareRow
                key={line}
                content={content}
                route={route}
                kind={kind}
                set={company.fares[line]}
                auto={company.auto.includes(line)}
                locked={Boolean(company.fareLockedUntil)}
                busy={busy}
                onAct={onAct}
              />
            );
          })}
        </section>
      )}
      <section className="business-sums" aria-label="The company">
        <p>
          <span>
            Engineers{' '}
            <small>
              · {formatPence(content.rules.engineerWagePence)} a week, each looks after {content.rules.engineerCovers}{' '}
              trains
            </small>
          </span>
          <span className="railco-stepper">
            <button
              type="button"
              aria-label="One engineer fewer"
              disabled={busy || company.engineers <= 0}
              onClick={() => onAct({ action: 'engineers', count: company.engineers - 1 })}
            >
              −
            </button>
            <strong>{company.engineers}</strong>
            <button
              type="button"
              aria-label="One engineer more"
              disabled={busy || company.engineers >= content.rules.maxEngineers}
              onClick={() => onAct({ action: 'engineers', count: company.engineers + 1 })}
            >
              +
            </button>
          </span>
        </p>
        <label className="railco-toggle">
          <input
            type="checkbox"
            checked={company.insured}
            disabled={busy}
            onChange={(e) => onAct({ action: 'insurance', on: e.target.checked })}
          />
          Insurance: a breakdown costs only the excess
        </label>
        <button type="button" className="flights-terminal" onClick={() => setRenaming(!renaming)}>
          ✏️ Rename or repaint
        </button>
        {renaming && <Rename content={content} company={company} busy={busy} onAct={onAct} />}
      </section>
      {company.events.length > 0 && (
        <section className="railco-events" aria-label="Lately">
          <strong className="business-step-title">LATELY</strong>
          <ul>
            {company.events.slice(0, 12).map((event, index) => (
              <li key={`${event.at}-${index}`}>
                <span>{eventLine(event)}</span>
                <small>{londonWhen(event.at)}</small>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

const eventLine = (event: RailCompanyView['events'][number]) => {
  const detail = event.detail as Record<string, unknown>;
  switch (event.kind) {
    case 'run':
      return `${String(detail.train)}: ${Number(detail.riders)} riders · ${event.amountPence >= 0 ? '+' : ''}${formatPence(event.amountPence)}${detail.broke ? ' · broke down' : ''}`;
    case 'bills':
      return `Bills paid · ${formatPence(event.amountPence)}${(detail.walked as string[] | undefined)?.length ? ` · crew walked off ${(detail.walked as string[]).join(', ')}` : ''}`;
    case 'skipped':
      return `${String(detail.train)}: not enough money for the energy, it stayed in`;
    case 'worn':
      return `${String(detail.train)}: worn out`;
    case 'bought':
      return `Bought a train · ${formatPence(event.amountPence)}`;
    case 'sold':
      return `${String(detail.train)} sold · ${formatPence(event.amountPence)}`;
    case 'registered':
      return `Registered · ${formatPence(event.amountPence)}`;
    default:
      return event.kind;
  }
};

function TrainCard({
  content,
  train,
  routes,
  routeName,
  walletPence,
  busy,
  onAct,
}: {
  content: RailCoContent;
  train: TrainView;
  routes: RailRoute[];
  routeName: (id: string) => string;
  walletPence: number;
  busy: boolean;
  onAct: (body: Parameters<typeof api.railco.train>[1]) => void;
}) {
  const type = content.trains[train.kind];
  const [open, setOpen] = useState(false);
  const [selling, setSelling] = useState(false);
  const status = train.parked ? PARKED[train.parked] : train.condition < content.rules.noStart ? 'Worn out' : 'Running';
  const optional = type.optional;
  return (
    <li className="railco-card">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        aria-label={`${train.tag} ${type.label}`}
      >
        <span className="business-icon" aria-hidden="true">
          {type.icon}
        </span>
        <span className="business-who">
          <strong>
            {train.tag} · {type.label}
          </strong>
          <small>King&apos;s Cross → {routeName(train.route)}</small>
          <small>
            {train.last
              ? `Last evening: ${train.last.riders} riders · ${train.last.netPence >= 0 ? '+' : ''}${formatPence(train.last.netPence)}${train.last.broke ? ' · broke down' : ''}`
              : 'First run at 6 PM'}
          </small>
        </span>
        <span className="business-till">
          <em className={`business-status ${train.parked ? 'shut' : ''}`}>{status}</em>
          <span
            className={`railco-condition ${conditionTone(train.condition)}`}
            role="meter"
            aria-label="Condition"
            aria-valuenow={train.condition}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <i style={{ width: `${train.condition}%` }} />
          </span>
        </span>
      </button>
      {open && (
        <div className="railco-actions">
          <small>
            Crew: {train.crew.length ? train.crew.map((role) => CREW_ROLE_LABELS[role]).join(', ') : 'none'} · condition{' '}
            {train.condition}%
          </small>
          <div className="business-chips">
            <button
              type="button"
              disabled={busy || train.condition >= 100 || walletPence < train.servicePence}
              onClick={() => onAct({ action: 'service' })}
            >
              {train.condition >= 100 ? 'Top condition' : `🔧 Service ${formatPence(train.servicePence)}`}
            </button>
            <button type="button" disabled={busy} onClick={() => onAct({ action: 'park', parked: !train.parked })}>
              {train.parked ? (train.parked === 'crew' ? '🧑‍✈️ Hire a crew' : '▶️ Back on the line') : '⏸ Park it'}
            </button>
            {optional && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onAct({ action: 'optional', on: !train.crew.includes(optional.role) })}
              >
                {train.crew.includes(optional.role)
                  ? `${CREW_ROLE_LABELS[optional.role]} off`
                  : `Hire a ${CREW_ROLE_LABELS[optional.role].toLowerCase()} · ${formatPence(optional.wagePence)}/wk`}
              </button>
            )}
          </div>
          <small>Move it to another route (costs one run&apos;s energy):</small>
          <div className="business-chips" role="group" aria-label="Move it to">
            {routes
              .filter((item) => item.placeId !== train.route)
              .map((item) => (
                <button
                  type="button"
                  key={item.placeId}
                  disabled={busy || walletPence < type.energyPence}
                  onClick={() => onAct({ action: 'move', route: item.placeId })}
                >
                  → {item.name} <small>{formatPence(type.energyPence)}</small>
                </button>
              ))}
          </div>
          {selling ? (
            <div className="jet-sell">
              <span>Sell it for {formatPence(train.resalePence)}?</span>
              <button type="button" onClick={() => setSelling(false)} disabled={busy}>
                Keep it
              </button>
              <button type="button" className="jet-sell-yes" disabled={busy} onClick={() => onAct({ action: 'sell' })}>
                Sell
              </button>
            </div>
          ) : (
            <button type="button" className="flights-terminal" onClick={() => setSelling(true)}>
              Sell · {formatPence(train.resalePence)}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function FareRow({
  content,
  route,
  kind,
  set,
  auto,
  locked,
  busy,
  onAct,
}: {
  content: RailCoContent;
  route: RailRoute;
  kind: TrainKind;
  set: number | undefined;
  auto: boolean;
  locked: boolean;
  busy: boolean;
  onAct: (body: Parameters<typeof api.railco.act>[0]) => void;
}) {
  const band = fareBand(content, route, kind);
  const current = set ?? band.ref;
  const [fare, setFare] = useState(current);
  const first = useRef(current);
  useEffect(() => {
    if (first.current !== current) setFare(current);
    first.current = current;
  }, [current]);
  const step = Math.max(10, Math.round((band.ref * 0.05) / 10) * 10);
  return (
    <div className="railco-fare-row">
      <span className="business-who">
        <strong>
          {content.trains[kind].icon} {route.name}
        </strong>
        <small>
          Going rate {formatPence(band.ref)} · {formatPence(band.low)}–{formatPence(band.high)}
        </small>
      </span>
      <span className="railco-stepper">
        <button
          type="button"
          aria-label={`${formatPence(step)} less`}
          disabled={busy || auto || fare <= band.low}
          onClick={() => setFare(Math.max(band.low, fare - step))}
        >
          −
        </button>
        <strong>{formatPence(fare)}</strong>
        <button
          type="button"
          aria-label={`${formatPence(step)} more`}
          disabled={busy || auto || fare >= band.high}
          onClick={() => setFare(Math.min(band.high, fare + step))}
        >
          +
        </button>
      </span>
      <div className="business-chips">
        <button
          type="button"
          disabled={busy || auto || locked || fare === current}
          onClick={() => onAct({ action: 'fare', route: route.placeId, kind, farePence: fare })}
        >
          Set {formatPence(fare)}
        </button>
        <button
          type="button"
          aria-pressed={auto}
          className={auto ? 'active' : ''}
          disabled={busy}
          onClick={() => onAct({ action: 'auto', route: route.placeId, kind, on: !auto })}
        >
          {auto ? '✓ Follows the week' : 'Follow the week'}
        </button>
      </div>
    </div>
  );
}

function Rename({
  content,
  company,
  busy,
  onAct,
}: {
  content: RailCoContent;
  company: RailCompanyView;
  busy: boolean;
  onAct: (body: Parameters<typeof api.railco.act>[0]) => void;
}) {
  const [name, setName] = useState(company.name);
  const [slogan, setSlogan] = useState(company.slogan ?? '');
  const [paint, setPaint] = useState({ body: company.body, stripe: company.stripe });
  const problem = name.trim() !== company.name ? nameProblem(content, name) : null;
  const changed = name.trim() !== company.name || slogan.trim() !== (company.slogan ?? '');
  const repainted = paint.body !== company.body || paint.stripe !== company.stripe;
  return (
    <div className="railco-rename">
      <label className="railco-field">
        <span>COMPANY NAME</span>
        <input value={name} maxLength={24} onChange={(e) => setName(e.target.value)} />
        {problem && <small className="railco-problem">{problem}</small>}
      </label>
      <label className="railco-field">
        <span>SLOGAN</span>
        <input value={slogan} maxLength={40} onChange={(e) => setSlogan(e.target.value)} />
      </label>
      <button
        type="button"
        className="flights-terminal"
        disabled={busy || !changed || Boolean(problem)}
        onClick={() => onAct({ action: 'rename', name: name.trim(), slogan: slogan.trim() || undefined })}
      >
        {name.trim() !== company.name ? `Rename · ${formatPence(content.rules.renamePence)}` : 'Save the slogan'}
      </button>
      <Liveries content={content} value={paint} onPick={setPaint} />
      <button
        type="button"
        className="flights-terminal"
        disabled={busy || !repainted}
        onClick={() => onAct({ action: 'repaint', ...paint })}
      >
        Repaint · {formatPence(content.rules.repaintPence * Math.max(1, company.trains.length))}
      </button>
    </div>
  );
}
